/**
 * SystemLogPanel — Shows error logs + activity logs for SysAdmins
 * Appears as a right-side panel (like SmartAssistant)
 * Tabs: Errors | Activity | Token Usage
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, AlertCircle, Activity, Coins } from 'lucide-react';
import { API_BASE as API_URL, getToken} from '../lib/api';


const SystemLogPanel = ({ isOpen, onToggle, theme = 'light' }) => {
  const isDark = theme === 'dark';
  const [tab, setTab] = useState('errors');
  const [errors, setErrors] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = getToken() || '';

  const loadData = useCallback(async () => {
    if (!token || !isOpen) return;
    setLoading(true);
    try {
      const [errRes, actRes, tokRes] = await Promise.all([
        fetch(`${API_URL}/api/system/error-log?page=1&pageSize=50`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/system/activity-log?page=1&pageSize=50`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/token-guard/usage?period=today`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [errJson, actJson, tokJson] = await Promise.all([errRes.json(), actRes.json(), tokRes.json()]);
      if (errJson.success) setErrors(errJson.data || []);
      if (actJson.success) setActivity(actJson.data || []);
      if (tokJson.success) setTokenUsage(tokJson.data || null);
    } catch {}
    setLoading(false);
  }, [token, isOpen]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isOpen) return null;

  const bg = isDark ? 'bg-[#111]' : 'bg-white';
  const border = isDark ? 'border-[#222]' : 'border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-500' : 'text-slate-400';

  const TABS = [
    { id: 'errors', label: 'Errors', icon: AlertCircle, count: errors.length, color: 'text-red-500' },
    { id: 'activity', label: 'Activity', icon: Activity, count: activity.length, color: 'text-blue-500' },
    { id: 'tokens', label: 'Tokens', icon: Coins, count: tokenUsage?.summary?.calls || 0, color: 'text-amber-500' },
  ];

  return (
    <div className={`fixed right-0 top-0 bottom-[40px] w-[380px] ${bg} border-l ${border} z-40 flex flex-col shadow-xl`} data-testid="system-log-panel">
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${border}`}>
        <span className={`text-xs font-bold ${text}`}>System Log</span>
        <div className="flex items-center gap-2">
          <button onClick={loadData} className={`p-1 rounded hover:bg-slate-100 ${muted}`}><RefreshCw size={13} className={loading ? 'animate-spin' : ''} /></button>
          <button onClick={onToggle} className={`p-1 rounded hover:bg-slate-100 ${muted}`}><X size={14} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${border}`}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-semibold border-b-2 transition-colors ${
              tab === t.id ? `${t.color} border-current` : `${muted} border-transparent hover:${text}`
            }`}>
            <t.icon size={12} /> {t.label} <span className={`px-1 py-0 rounded text-[8px] ${tab === t.id ? 'bg-current/10' : isDark ? 'bg-[#222]' : 'bg-slate-100'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {tab === 'errors' && (
          <div className="divide-y divide-slate-100">
            {errors.length === 0 ? (
              <div className={`p-6 text-center ${muted} text-xs`}>No errors logged</div>
            ) : errors.map((err, i) => (
              <div key={i} className="px-3 py-2 hover:bg-red-50/50">
                <div className="flex items-start gap-2">
                  <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-red-600 truncate">{err.ScreenName}</p>
                    <p className="text-[10px] text-slate-600 mt-0.5 line-clamp-2">{err.EMessage}</p>
                    <p className={`text-[9px] ${muted} mt-0.5`}>{err.ProcedureName}</p>
                    {err.MDate && <p className={`text-[9px] ${muted} mt-0.5`}>{new Date(err.MDate).toLocaleString()}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'activity' && (
          <div className="divide-y divide-slate-100">
            {activity.length === 0 ? (
              <div className={`p-6 text-center ${muted} text-xs`}>No activity logged</div>
            ) : activity.map((act, i) => (
              <div key={i} className="px-3 py-2 hover:bg-blue-50/50">
                <div className="flex items-start gap-2">
                  <Activity size={12} className="text-blue-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-blue-600">{act.Action}</span>
                      <span className={`text-[9px] ${muted}`}>{act.Module}</span>
                    </div>
                    <p className="text-[10px] text-slate-600 mt-0.5">{act.Details}</p>
                    <p className={`text-[9px] ${muted}`}>User: {act.UserID} | {act.ScreenID}</p>
                    {act.CreatedAt && <p className={`text-[9px] ${muted}`}>{new Date(act.CreatedAt).toLocaleString()}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'tokens' && (
          <div className="p-3 space-y-3">
            {tokenUsage?.summary && (
              <div className={`grid grid-cols-3 gap-2`}>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-amber-50'} text-center`}>
                  <p className="text-[9px] text-amber-600 font-semibold">Calls</p>
                  <p className="text-sm font-bold text-amber-700">{tokenUsage.summary.calls || 0}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-blue-50'} text-center`}>
                  <p className="text-[9px] text-blue-600 font-semibold">Tokens</p>
                  <p className="text-sm font-bold text-blue-700">{(tokenUsage.summary.totalTokens || 0).toLocaleString()}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-green-50'} text-center`}>
                  <p className="text-[9px] text-green-600 font-semibold">Cost</p>
                  <p className="text-sm font-bold text-green-700">${(tokenUsage.summary.totalCost || 0).toFixed(4)}</p>
                </div>
              </div>
            )}
            {tokenUsage?.byTask?.length > 0 && (
              <div>
                <p className={`text-[10px] font-semibold ${text} mb-1`}>By Task Type</p>
                {tokenUsage.byTask.map((t, i) => (
                  <div key={i} className={`flex items-center justify-between py-1 text-[10px] ${border} border-b`}>
                    <span className={text}>{t.TaskType}</span>
                    <span className={muted}>{t.totalTokens} tokens / {t.calls} calls</span>
                  </div>
                ))}
              </div>
            )}
            {tokenUsage?.recent?.length > 0 && (
              <div>
                <p className={`text-[10px] font-semibold ${text} mb-1`}>Recent</p>
                {tokenUsage.recent.slice(0, 10).map((r, i) => (
                  <div key={i} className={`py-1 text-[9px] ${border} border-b`}>
                    <span className={text}>{r.TaskType}</span> <span className={muted}>— {r.TokensUsed} tokens — {r.ModuleName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemLogPanel;
